import { FocusEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Upload } from 'antd';
import { useTranslation } from 'react-i18next';
import { showErrorToast, showSuccessToast } from '@/components';
import { IContentBlockInstance, IContentPage } from '@/models/content-page.model';
import { CONTENT_PAGE_CREATE_ID } from '@/models/blog-post.model';
import { SupportedLocales } from '@/models/localization.model';
import { ROUTES } from '@/models/router.model';
import { MAX_IMAGE_SIZE } from '@/constants/files';
import { env } from '@/config/env';
import { formatApiError, validateFileSize } from '@/utils';
import { useAppDispatch, useAppState } from '@/store';
import { useGetLocales } from '@/modules/Locales';
import {
  addBlock,
  applyInspectorBlock,
  contentPagesSelector,
  getBlogCategories,
  getContentBlockSchemas,
  getContentPageById,
  patchMeta,
  removeBlock as removeBlockAction,
  reorderBlocks,
  revertContentPage,
  saveContentPage,
  selectBlogCategoryOptions,
  selectInspectorBlock,
  selectSchemaByType,
  selectSelectedBlock,
  selectSelectedSchema,
  setBlogCategoryId,
  setContentPagePublished,
  setCoverImage,
  setCoverUploading,
  setEditorPage,
  setLocaleTitle,
  setLocaleDescription,
  setMetaFieldErrors,
  setPinned,
  setSelectedBlockId,
  syncAutosavePayload,
  syncPageByLocale,
  triggerContentPageAutosave,
} from '@/modules/ContentPages';
import { resolveBlogMediaDisplayUrl, uploadBlogImageFile } from '../helpers/blogMedia';
import { buildBlogPostStorePayload, createEmptyBlogContentPage, resolveActiveLocale } from '../helpers/blogPostMapper';
import { validateBlogPostPage } from '../helpers/validateBlogPostEditor';
import { validatePageContentBlocks } from '../helpers/validateContentBlockSchema';

const normalizeEditorLocales = (rawLocales: SupportedLocales[] | null): SupportedLocales[] => {
  const source = rawLocales?.length ? rawLocales : [SupportedLocales.UK, SupportedLocales.RU];
  const normalized = source.map((locale) => (locale === ('ua' as SupportedLocales) ? SupportedLocales.UK : locale));
  const filtered = normalized.filter(
    (locale): locale is SupportedLocales => locale === SupportedLocales.UK || locale === SupportedLocales.RU,
  );
  const unique = Array.from(new Set(filtered));
  if (!unique.length) return [SupportedLocales.UK, SupportedLocales.RU];
  if (!unique.includes(SupportedLocales.UK)) unique.unshift(SupportedLocales.UK);
  return unique;
};

export const useContentPageEditor = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation(['contentPages', 'common']);
  const localesFromStore = useGetLocales();
  const locales = useMemo(() => normalizeEditorLocales(localesFromStore), [localesFromStore]);
  const activeLocale = useMemo(() => resolveActiveLocale(i18n.language), [i18n.language]);
  const { editor, aux, error } = useAppState(contentPagesSelector);
  const schemaByType = useAppState(selectSchemaByType);
  const selectedBlock = useAppState(selectSelectedBlock);
  const selectedSchema = useAppState(selectSelectedSchema);
  const inspectorBlock = useAppState(
    useMemo(() => selectInspectorBlock(activeLocale, locales), [activeLocale, locales]),
  );
  const blogCategoryOptions = useAppState(useMemo(() => selectBlogCategoryOptions(activeLocale), [activeLocale]));
  const websiteBase = useMemo(() => env.WEBSITE_URL.replace(/\/$/, ''), []);
  const previewSlug = (editor.page?.localeMeta?.[activeLocale]?.slug ?? '').trim();
  const previewUrl = previewSlug ? `${websiteBase}/blog/post/preview/${encodeURIComponent(previewSlug)}` : '';
  const canPreview = Boolean(websiteBase && previewSlug);
  const previewTitle = !websiteBase
    ? t('content_pages_list_action_preview_no_website_url', { ns: 'common' })
    : !previewSlug
    ? t('content_pages_editor_preview_no_slug')
    : t('content_pages_list_action_preview', { ns: 'common' });
  const [coverPreviewUrl, setCoverPreviewUrl] = useState<string | null>(null);
  const patchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    dispatch(getContentBlockSchemas())
      .unwrap()
      .catch(() => showErrorToast({ message: t('schemas_load_error') }));
    dispatch(getBlogCategories());
  }, [dispatch, t]);

  useEffect(() => {
    dispatch(setMetaFieldErrors(null));
  }, [dispatch, id]);

  useEffect(() => {
    if (!id) {
      dispatch(setEditorPage(null));
      return;
    }
    if (id === CONTENT_PAGE_CREATE_ID) {
      dispatch(setEditorPage(createEmptyBlogContentPage(activeLocale, id)));
      return;
    }
    dispatch(getContentPageById({ id }))
      .unwrap()
      .then(() => dispatch(syncPageByLocale(activeLocale)))
      .catch(() => showErrorToast({ message: t('load_error') }));
  }, [activeLocale, dispatch, id, t]);

  useEffect(() => {
    dispatch(syncPageByLocale(activeLocale));
  }, [activeLocale, dispatch]);

  useEffect(() => {
    return () => {
      if (coverPreviewUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(coverPreviewUrl);
      }
    };
  }, [coverPreviewUrl]);

  useEffect(() => {
    if (!id || id === CONTENT_PAGE_CREATE_ID || !editor.page) return;
    const serializedPayload = JSON.stringify(buildBlogPostStorePayload(editor.page));
    dispatch(syncAutosavePayload(serializedPayload));
    if (patchTimerRef.current) clearTimeout(patchTimerRef.current);
    patchTimerRef.current = setTimeout(() => {
      void dispatch(triggerContentPageAutosave({ id }));
    }, 800);
  }, [dispatch, editor.page, id]);

  useEffect(() => {
    return () => {
      if (patchTimerRef.current) clearTimeout(patchTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!error) return;
    showErrorToast({ message: error.message || t('common_error', { ns: 'common' }) });
  }, [error, t]);

  const handleSave = useCallback(async () => {
    if (!editor.page || !id) return;
    const validationError = validateBlogPostPage(editor.page, locales);
    if (validationError) {
      dispatch(
        setMetaFieldErrors({
          invalidNameLocales: validationError.invalidNameLocales,
          invalidSlugLocales: validationError.invalidSlugLocales,
        }),
      );
      showErrorToast({ message: t(validationError.messageKey, validationError.values) });
      return;
    }
    dispatch(setMetaFieldErrors(null));
    const blockValidationError = validatePageContentBlocks(editor.page, schemaByType, locales);
    if (blockValidationError) {
      if (blockValidationError.blockId) dispatch(setSelectedBlockId(blockValidationError.blockId));
      showErrorToast({ message: t(blockValidationError.messageKey, blockValidationError.values) });
      return;
    }
    const payload = buildBlogPostStorePayload(editor.page);
    try {
      const result = await dispatch(saveContentPage({ id, payload })).unwrap();
      if ('created' in result) {
        showSuccessToast({ message: t('save_success') });
        navigate(ROUTES.CONTENT_PAGE_DETAILS.replace(':id', result.createdId), { replace: true });
      } else {
        dispatch(syncPageByLocale(activeLocale));
        showSuccessToast({ message: t('save_success') });
      }
    } catch {
      showErrorToast({ message: t('save_error') });
    }
  }, [activeLocale, dispatch, editor.page, id, locales, navigate, schemaByType, t]);

  const handleCancelDraft = useCallback(() => {
    dispatch(setMetaFieldErrors(null));
    dispatch(setSelectedBlockId(null));
    setCoverPreviewUrl((current) => {
      if (current?.startsWith('blob:')) URL.revokeObjectURL(current);
      return null;
    });

    if (!id) return;

    if (id === CONTENT_PAGE_CREATE_ID) {
      dispatch(setEditorPage(createEmptyBlogContentPage(activeLocale, id)));
      return;
    }

    void dispatch(revertContentPage({ id }))
      .unwrap()
      .then(() => {
        dispatch(syncPageByLocale(activeLocale));
        showSuccessToast({ message: t('editor_cancel_success') });
      })
      .catch(() => showErrorToast({ message: t('save_error') }));
  }, [activeLocale, dispatch, id, t]);

  const handlePublishedChange = useCallback(
    async (checked: boolean) => {
      if (!id || id === CONTENT_PAGE_CREATE_ID) return;
      try {
        await dispatch(setContentPagePublished({ id, published: checked })).unwrap();
      } catch {
        showErrorToast({ message: t('save_error') });
      }
    },
    [dispatch, id, t],
  );

  const handleEditorBlurCapture = useCallback(
    (e: FocusEvent<HTMLElement>) => {
      const nextFocusedNode = e.relatedTarget as Node | null;
      if (nextFocusedNode && e.currentTarget.contains(nextFocusedNode)) return;
      if (patchTimerRef.current) clearTimeout(patchTimerRef.current);
      if (id) void dispatch(triggerContentPageAutosave({ id }));
    },
    [dispatch, id],
  );

  const handleCoverDrop = useCallback(
    (file: File) => {
      if (!file.type.startsWith('image/')) return Upload.LIST_IGNORE;
      if (!validateFileSize(file.size, MAX_IMAGE_SIZE)) {
        showErrorToast({ message: t('error_image_too_large', { limit: MAX_IMAGE_SIZE / 1024 / 1024 }) });
        return Upload.LIST_IGNORE;
      }
      const blobUrl = URL.createObjectURL(file);
      setCoverPreviewUrl((current) => {
        if (current?.startsWith('blob:')) URL.revokeObjectURL(current);
        return blobUrl;
      });
      dispatch(setCoverUploading(true));
      void (async () => {
        try {
          const url = await uploadBlogImageFile(file);
          dispatch(setCoverImage(resolveBlogMediaDisplayUrl(url) || url));
          setCoverPreviewUrl((current) => {
            if (current?.startsWith('blob:')) URL.revokeObjectURL(current);
            return null;
          });
        } catch (e) {
          showErrorToast({ message: formatApiError(e).message });
        } finally {
          dispatch(setCoverUploading(false));
        }
      })();
      return false;
    },
    [dispatch, t],
  );

  const handleAddBlock = useCallback(
    (blockType: string) => dispatch(addBlock({ blockType, activeLocale, locales })),
    [activeLocale, dispatch, locales],
  );
  const handleRemoveBlock = useCallback(
    (blockId: string) => dispatch(removeBlockAction({ blockId, activeLocale, locales })),
    [activeLocale, dispatch, locales],
  );
  const handleReorder = useCallback(
    (blocks: IContentBlockInstance[]) => dispatch(reorderBlocks({ blocks, locales })),
    [dispatch, locales],
  );
  const handleInspectorChange = useCallback(
    (next: IContentBlockInstance) => dispatch(applyInspectorBlock({ next, activeLocale, locales })),
    [activeLocale, dispatch, locales],
  );
  const handleMetaChange = useCallback(
    (patch: Partial<Pick<IContentPage, 'pageType' | 'title' | 'tags'>>) => dispatch(patchMeta({ patch, activeLocale })),
    [activeLocale, dispatch],
  );

  return {
    id,
    t,
    locales,
    activeLocale,
    editor,
    aux,
    selectedBlock,
    selectedSchema,
    inspectorBlock,
    schemaByType,
    blogCategoryOptions,
    previewUrl,
    canPreview,
    previewTitle,
    coverPreviewUrl,
    handleSave,
    handleCancelDraft,
    handlePublishedChange,
    handleEditorBlurCapture,
    handleCoverDrop,
    handleAddBlock,
    handleRemoveBlock,
    handleReorder,
    handleInspectorChange,
    handleMetaChange,
    setSelectedBlockId: (value: string | null) => dispatch(setSelectedBlockId(value)),
    setLocaleTitle: (locale: SupportedLocales, value: string) =>
      dispatch(setLocaleTitle({ locale, value, activeLocale })),
    setLocaleDescription: (locale: SupportedLocales, value: string) =>
      dispatch(setLocaleDescription({ locale, value })),
    setPinned: (checked: boolean) => dispatch(setPinned(checked)),
    setBlogCategoryId: (value: string | null) => dispatch(setBlogCategoryId(value)),
  };
};
